import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { attentionKeys } from '~/lib/query-keys';

/**
 * FRONT-1485: what is waiting on this user, for the sidebar's badges.
 *
 * Replaces `use-resource-counts.ts`, which returned inventory totals (how many
 * memory entries, files, agents, apps, skills, tools you had). Those read like
 * unread mail in a nav rail while actually being stock levels, and two of them
 * overstated their own page — see `src/admin/counts-routes.ts` for the numbers.
 *
 * A badge here means "there is something here for you to decide". Anything
 * else belongs on the page it describes.
 */
export interface Attention {
  /** Memory inbox: pending reflect proposals (minus commitments) + duplicate reviews. */
  memoryReview: number | null;
  /** Work: pending commitments awaiting a yes/no. */
  workNeedsOk: number | null;
  /** Knowledge: items that may be out of date (same count as the Home card). */
  knowledgeReview: number | null;
}

export function useAttention() {
  return useQuery<Attention>({
    queryKey: attentionKeys.all,
    queryFn: () => api.get<Attention>('/api/attention'),
    // FRONT-1488: three refresh triggers, because one was not enough.
    //  - invalidation from the mutations that clear these queues (instant, and
    //    the case the user actually notices: you approve something and the
    //    badge must stop claiming it);
    //  - focus, overriding the app-wide `refetchOnWindowFocus: false`, so a
    //    change made in another tab, on the phone, or by a background reflect
    //    run lands when you come back rather than needing a reload;
    //  - the poll, kept as the backstop for a tab left open and untouched.
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}
